"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, readFragment, useQuery } from "@opensea/graphql";
import { Link } from "@opensea/next-ui-kit";
import { AnimatedNumber, CenterAligned, classNames, FlexColumn, IconCallout, List, Modal, ModalBody, ModalHeader, ModalHeaderTitle, TextBody, useIsLessThanMd } from "@opensea/ui-kit";
import { AccountBalanceWallet } from "@opensea/ui-kit/icons";
import { isAddressEqual, normalizeAddress, TTL } from "@opensea/utils";
import { useDisableAccountSync, usePreviouslyConnectedAccounts } from "@opensea/wallet";
import { keyBy } from "es-toolkit";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useContextSelector } from "use-context-selector";
import { AccountSwitcherAccountItemsFragment } from "@/components/navigation/top-nav/account-switcher/AccountSwitcherAccountItems.fragment";
import { AsteriskText } from "@/components/profile/AsteriskText";
import { ProfileSettingsContext } from "@/components/profile/ProfileSettingsProvider";
import { withSuspense } from "@/components/suspense/WithSuspense";
import { useAddress, useAuthenticated } from "@/hooks/wallet";
import { useLinkedWalletModalStore } from "@/stores/use-linked-wallet-modal-store";
import { AccountSwitcherTopAccount } from "../../navigation/top-nav/account-switcher/AccountSwitcherTopAccount";
import { LinkedAccountSwitcherItem } from "../../navigation/top-nav/account-switcher/LinkedAccountSwitcherItem";
import { LinkedWalletModalFooter } from "./LinkedWalletModalFooter";
import { LinkedWalletsSkeletons } from "./LinkedWalletsSkeletons";
import { LinkNewWalletModalContent } from "./LinkWalletFlow/LinkNewWalletModalContent";
const query = graphql(`
    query LinkedWalletModalQuery($address: Address!) {
      profilesByAccount(address: $address) {
        profiles {
          ... on Profile {
            ...AccountSwitcherAccountItems
          }
        }
        account {
          ... on Profile {
            ...AccountSwitcherAccountItems
          }
        }
      }
      walletsByAccount {
        items {
          address
          lastSeenConnectorId
        }
      }
    }
  `, [AccountSwitcherAccountItemsFragment]);
export const LinkedWalletModal = withSuspense(() => {
  const address = useAddress();
  const {
    isLinkedWalletModalOpen,
    setIsLinkedWalletModalOpen,
    setIsAddingNewWallet,
    isAddingNewWallet
  } = useLinkedWalletModalStore();
  const [connector, setConnector] = useState();
  if (!address) {
    return null;
  }
  return <Modal className={classNames({
    "max-w-[420px]": isAddingNewWallet && connector === undefined
  })} content={<LinkedWalletModalContent address={address} connector={connector} setConnector={setConnector} />} disableWidthTransition onBack={isAddingNewWallet && connector !== undefined ? () => {
    setConnector(undefined);
  } : undefined} onInteractOutside={e => {
    if (isAddingNewWallet && connector !== undefined) {
      e.preventDefault();
    }
  }} onOpenChange={open => {
    setIsLinkedWalletModalOpen(open);
    if (!open) {
      // Reset adding state when modal closes
      setIsAddingNewWallet(false);
      setConnector(undefined);
    }
  }} open={isLinkedWalletModalOpen} withCloseIcon />;
}, {
  fallback: false,
  ssr: false
});
const CONTEXT = {
  suspense: false,
  ttl: TTL["15s"]
};
function LinkedWalletModalContent(t0) {
  const $ = _c(86);
  const {
    address,
    connector,
    setConnector
  } = t0;
  const t = useTranslations("LinkedWalletModal");
  const isMobile = useIsLessThanMd();
  let t1;
  if ($[0] !== address) {
    t1 = {
      query,
      variables: {
        address
      },
      context: CONTEXT
    };
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      throwOnError: false
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [t3, refetch] = useQuery(t1, t2);
  const {
    data,
    fetching
  } = t3;
  useDisableAccountSync();
  const previouslyConnectedAccounts = usePreviouslyConnectedAccounts();
  let t4;
  if ($[3] !== previouslyConnectedAccounts) {
    t4 = keyBy(previouslyConnectedAccounts, _temp);
    $[3] = previouslyConnectedAccounts;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const previouslyConnectedAccountsByAddress = t4;
  const {
    isAddingNewWallet,
    setIsAddingNewWallet,
    setIsLinkedWalletModalOpen
  } = useLinkedWalletModalStore();
  const isAuthenticated = useAuthenticated();
  let t5;
  if ($[5] !== isAuthenticated || $[6] !== setIsLinkedWalletModalOpen) {
    t5 = () => {
      if (!isAuthenticated) {
        setIsLinkedWalletModalOpen(false);
      }
    };
    $[5] = isAuthenticated;
    $[6] = setIsLinkedWalletModalOpen;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isAuthenticated) {
    t6 = [isAuthenticated];
    $[8] = isAuthenticated;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffect(t5, t6);
  const hideWalletWorth = useContextSelector(ProfileSettingsContext, _temp2);
  let t7;
  if ($[10] !== hideWalletWorth) {
    t7 = function renderUsdBalance(value) {
      if (hideWalletWorth) {
        return <AsteriskText />;
      }
      return <AnimatedNumber display="usd" value={value} />;
    };
    $[10] = hideWalletWorth;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const renderUsdBalance = t7;
  let t8;
  if ($[12] !== data?.profilesByAccount?.profiles) {
    t8 = data?.profilesByAccount?.profiles ?? [];
    $[12] = data?.profilesByAccount?.profiles;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let T0;
  let T1;
  let linkedWallets;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t9;
  if ($[14] !== connector || $[15] !== data?.profilesByAccount?.account || $[16] !== data?.walletsByAccount?.items || $[17] !== fetching || $[18] !== isAddingNewWallet || $[19] !== isMobile || $[20] !== previouslyConnectedAccountsByAddress || $[21] !== refetch || $[22] !== renderUsdBalance || $[23] !== setConnector || $[24] !== setIsAddingNewWallet || $[25] !== setIsLinkedWalletModalOpen || $[26] !== t || $[27] !== t8) {
    t16 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const profilesByAccount = t8.filter(_temp3).map(_temp4).sort(_temp5);
      let topAccount;
      const topAccountRaw = data?.profilesByAccount?.account;
      if (topAccountRaw && topAccountRaw.__typename === "Profile") {
        topAccount = readFragment(AccountSwitcherAccountItemsFragment, topAccountRaw);
      }
      let t17;
      if ($[39] !== data?.walletsByAccount?.items) {
        t17 = data?.walletsByAccount?.items ?? [];
        $[39] = data?.walletsByAccount?.items;
        $[40] = t17;
      } else {
        t17 = $[40];
      }
      let t18;
      if ($[41] !== t17) {
        t18 = new Map(t17.map(_temp6));
        $[41] = t17;
        $[42] = t18;
      } else {
        t18 = $[42];
      }
      const walletConnectorIdsByAddress = t18;
      linkedWallets = profilesByAccount.map(_temp7);
      if (isAddingNewWallet) {
        let t19;
        if ($[43] !== refetch || $[44] !== setConnector || $[45] !== setIsAddingNewWallet) {
          t19 = () => {
            refetch({
              requestPolicy: "network-only"
            });
            setIsAddingNewWallet(false);
            setConnector(undefined);
          };
          $[43] = refetch;
          $[44] = setConnector;
          $[45] = setIsAddingNewWallet;
          $[46] = t19;
        } else {
          t19 = $[46];
        }
        let t20;
        if ($[47] !== setIsLinkedWalletModalOpen) {
          t20 = () => {
            setIsLinkedWalletModalOpen(false);
          };
          $[47] = setIsLinkedWalletModalOpen;
          $[48] = t20;
        } else {
          t20 = $[48];
        }
        let t21;
        if ($[49] !== connector || $[50] !== linkedWallets || $[51] !== setConnector || $[52] !== t19 || $[53] !== t20) {
          t21 = <LinkNewWalletModalContent connector={connector} linkedWallets={linkedWallets} onLinked={t19} onLoggedIn={t20} setConnector={setConnector} />;
          $[49] = connector;
          $[50] = linkedWallets;
          $[51] = setConnector;
          $[52] = t19;
          $[53] = t20;
          $[54] = t21;
        } else {
          t21 = $[54];
        }
        t16 = t21;
        break bb0;
      }
      let t19;
      if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = <IconCallout className="rounded-full" icon={AccountBalanceWallet} size={36} variant="outlined" />;
        $[55] = t19;
      } else {
        t19 = $[55];
      }
      let t20;
      if ($[56] !== t) {
        t20 = t("title");
        $[56] = t;
        $[57] = t20;
      } else {
        t20 = $[57];
      }
      if ($[58] !== t20) {
        t15 = <ModalHeader className="justify-center"><CenterAligned className="gap-4">{t19}<ModalHeaderTitle>{t20}</ModalHeaderTitle></CenterAligned></ModalHeader>;
        $[58] = t20;
        $[59] = t15;
      } else {
        t15 = $[59];
      }
      T1 = ModalBody;
      t13 = "flex flex-1 flex-col gap-6 overflow-hidden text-center";
      let t21;
      if ($[60] !== t) {
        t21 = t.rich("description", {
          learnMore: _temp8
        });
        $[60] = t;
        $[61] = t21;
      } else {
        t21 = $[61];
      }
      if ($[62] !== t21) {
        t14 = <FlexColumn className="gap-2 shrink-0"><TextBody color="text-secondary">{t21}</TextBody></FlexColumn>;
        $[62] = t21;
        $[63] = t14;
      } else {
        t14 = $[63];
      }
      T0 = FlexColumn;
      t9 = "gap-2 flex-1 min-h-0 overflow-y-auto";
      t10 = topAccount ? <div className="mb-2 rounded-lg border border-border-1 bg-bg-primary"><AccountSwitcherTopAccount account={topAccount} isLink={false} usdTotalValue={profilesByAccount.reduce(_temp9, 0)} variant="full" walletCount={profilesByAccount.length} /></div> : null;
      let t22;
      if ($[64] !== t) {
        t22 = t("yourLinkedWallets");
        $[64] = t;
        $[65] = t22;
      } else {
        t22 = $[65];
      }
      if ($[66] !== t22) {
        t11 = <TextBody className="text-left" size="sm" weight="semibold">{t22}</TextBody>;
        $[66] = t22;
        $[67] = t11;
      } else {
        t11 = $[67];
      }
      t12 = fetching ? <LinkedWalletsSkeletons /> : profilesByAccount.length > 0 ? <List className="rounded-lg border border-border-1 bg-bg-primary" showBorder={false}>{profilesByAccount.map(profile_0 => {
          const normalizedAddress = normalizeAddress(profile_0.address);
          const lastSeenConnectorId = walletConnectorIdsByAddress.get(normalizedAddress);
          const previouslyConnectedAccount = previouslyConnectedAccountsByAddress[normalizedAddress];
          const connectorId = lastSeenConnectorId || previouslyConnectedAccount?.connectorId;
          let isPrimary = false;
          if (topAccount) {
            const topAccountData = readFragment(AccountSwitcherAccountItemsFragment, topAccount);
            isPrimary = isAddressEqual(profile_0.address, topAccountData.address);
          }
          return <LinkedAccountSwitcherItem address={profile_0.address} avatarSize={20} className="h-16 px-4 md:h-12" connectorId={connectorId} description={renderUsdBalance(profile_0.portfolioSummary?.estimatedTokenValue?.usd ?? undefined)} displayName={profile_0.displayName} imageUrl={profile_0.imageUrl} isLink={false} isPrimary={isPrimary} key={profile_0.address} onClick={undefined} onMakePrimary={() => {
            refetch({
              requestPolicy: "network-only"
            });
          }} onUnlink={() => {
            refetch({
              requestPolicy: "network-only"
            });
          }} showActions={true} showCheck={false} variant={isMobile ? "compact" : "full"} />;
        })}</List> : <TextBody color="text-secondary" size="sm">{t("noWalletsLinked")}</TextBody>;
    }
    $[14] = connector;
    $[15] = data?.profilesByAccount?.account;
    $[16] = data?.walletsByAccount?.items;
    $[17] = fetching;
    $[18] = isAddingNewWallet;
    $[19] = isMobile;
    $[20] = previouslyConnectedAccountsByAddress;
    $[21] = refetch;
    $[22] = renderUsdBalance;
    $[23] = setConnector;
    $[24] = setIsAddingNewWallet;
    $[25] = setIsLinkedWalletModalOpen;
    $[26] = t;
    $[27] = t8;
    $[28] = T0;
    $[29] = T1;
    $[30] = linkedWallets;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t9;
  } else {
    T0 = $[28];
    T1 = $[29];
    linkedWallets = $[30];
    t10 = $[31];
    t11 = $[32];
    t12 = $[33];
    t13 = $[34];
    t14 = $[35];
    t15 = $[36];
    t16 = $[37];
    t9 = $[38];
  }
  if (t16 !== Symbol.for("react.early_return_sentinel")) {
    return t16;
  }
  let t17;
  if ($[68] !== T0 || $[69] !== t10 || $[70] !== t11 || $[71] !== t12 || $[72] !== t9) {
    t17 = <T0 className={t9}>{t10}{t11}{t12}</T0>;
    $[68] = T0;
    $[69] = t10;
    $[70] = t11;
    $[71] = t12;
    $[72] = t9;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  let t18;
  if ($[74] !== T1 || $[75] !== t13 || $[76] !== t14 || $[77] !== t17) {
    t18 = <T1 className={t13}>{t14}{t17}</T1>;
    $[74] = T1;
    $[75] = t13;
    $[76] = t14;
    $[77] = t17;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  let t19;
  if ($[79] !== fetching || $[80] !== linkedWallets.length) {
    t19 = <LinkedWalletModalFooter fetching={fetching} linkedWalletsCount={linkedWallets.length} />;
    $[79] = fetching;
    $[80] = linkedWallets.length;
    $[81] = t19;
  } else {
    t19 = $[81];
  }
  let t20;
  if ($[82] !== t15 || $[83] !== t18 || $[84] !== t19) {
    t20 = <>{t15}{t18}{t19}</>;
    $[82] = t15;
    $[83] = t18;
    $[84] = t19;
    $[85] = t20;
  } else {
    t20 = $[85];
  }
  return t20;
}
function _temp9(sum, p_0) {
  return sum + (p_0.portfolioSummary?.estimatedTokenValue?.usd ?? 0);
}
function _temp8(chunks) {
  return <Link href="https://support.opensea.io/articles/12270237-how-can-i-link-multiple-wallets-to-one-opensea-account" rel="noopener noreferrer" target="_blank">{chunks}</Link>;
}
function _temp7(p) {
  return p.address;
}
function _temp6(wallet) {
  return [normalizeAddress(wallet.address), wallet.lastSeenConnectorId];
}
function _temp5(a, b) {
  const aUsd = a.portfolioSummary?.estimatedTokenValue?.usd ?? -1;
  const bUsd = b.portfolioSummary?.estimatedTokenValue?.usd ?? -1;
  return bUsd - aUsd;
}
function _temp4(profileData) {
  return readFragment(AccountSwitcherAccountItemsFragment, profileData);
}
function _temp3(profile) {
  return profile?.__typename === "Profile";
}
function _temp2(state) {
  return state.hideWalletWorth;
}
function _temp(account) {
  return normalizeAddress(account.address);
}